import { AvailabilityRule, AvailabilityAction } from '@/types/revenue-manager';

/**
 * API format for availability rule
 */
export interface ApiAvailabilityRule {
  id?: string;
  name: string;
  org_id: string;
  property_id: number;
  priority: number;
  isActive: boolean;
  ruletype: 'availability';
  action: Array<{
    adjustmentType?: 'setStatus' | 'setMinStay' | 'setMaxStay' | 'setExactStay' | 'setAllotment' | 'adjustAllotment' | 'setAvailability' | 'adjustAvailability';
    adjustmentValue?: string | number;
    type?: 'setStatus' | 'setMinStay' | 'setMaxStay' | 'setExactStay' | 'setAllotment' | 'adjustAllotment' | 'setAvailability' | 'adjustAvailability'; // legacy field name
    value?: string | number; // legacy field name
  }>;
  roomTypes: number[];
  roomVariants: number[];
  otaChannels: string[];
  applicable_cm_names: string[];
  applicableTo: {
    startDate: string;
    endDate: string | null;
    daysOfWeek: string[] | null;
    timeOfDay: string | null;
  };
  executionType: {
    processingFrequency: 'manual' | 'daily' | 'weekly' | 'monthly' | 'specific' | 'immediate' | 'hourly';
    executionDays: number[];
    executionTime: string | null;
    executionStartDate: string | null;
    executionEndDate: string | null;
  };
  submitted_by?: string;
  created_at?: string;
  updated_at?: string;
  last_executed_at?: string;
}

/**
 * Raw API response format (can have either snake_case or camelCase)
 */
export interface RawApiAvailabilityRule {
  id?: string;
  name?: string;
  org_id?: string;
  property_id?: number;
  propertyId?: number;
  priority?: number;
  isActive?: boolean;
  is_active?: boolean;
  ruletype?: string;
  rule_type?: string;
  action?: Array<{
    adjustmentType?: 'setStatus' | 'setMinStay' | 'setMaxStay' | 'setExactStay' | 'setAllotment' | 'adjustAllotment' | 'setAvailability' | 'adjustAvailability';
    adjustmentValue?: string | number;
    type?: 'setStatus' | 'setMinStay' | 'setMaxStay' | 'setExactStay' | 'setAllotment' | 'adjustAllotment' | 'setAvailability' | 'adjustAvailability'; // legacy field name
    value?: string | number; // legacy field name
  }>;
  roomTypes?: number[];
  room_types?: number[];
  roomVariants?: number[];
  room_variants?: number[];
  otaChannels?: string[];
  ota_channels?: string[];
  applicable_cm_names?: string[];
  applicable_cm_name?: string;
  applicateCmNames?: string[];
  applicableTo?: {
    startDate?: string;
    endDate?: string | null;
    daysOfWeek?: string[] | null;
    timeOfDay?: string | null;
  };
  applicable_to?: {
    startDate?: string;
    endDate?: string | null;
    daysOfWeek?: string[] | null;
    timeOfDay?: string | null;
  };
  executionType?: {
    processingFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly' | 'specific' | 'immediate' | 'hourly';
    executionDays?: number[];
    executionTime?: string | null;
    executionStartDate?: string | null;
    executionEndDate?: string | null;
  };
  execution_type?: {
    processingFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly' | 'specific' | 'immediate' | 'hourly';
    executionDays?: number[];
    executionTime?: string | null;
    executionStartDate?: string | null;
    executionEndDate?: string | null;
  };
  submitted_by?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  last_executed_at?: string;
  lastExecutedAt?: string;
}

/**
 * Day of week mapping for API format
 */
const daysOfWeekMap: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

/**
 * Transform availability rule from dialog format to API format
 */
export function transformAvailabilityRuleForAPI(
  rule: Omit<AvailabilityRule, 'id' | 'createdAt' | 'updatedAt'> & { applicateCmNames?: string[] },
  orgId: string | number,
  propertyId: string | number,
  branchId: string | number,
  api_key: string | number,
): ApiAvailabilityRule {
  // Transform days of week
  const daysOfWeek = rule.daysOfWeek?.map(dayIndex => daysOfWeekMap[dayIndex]) || [];

  // Build the API format
  // If api_key is available, set otaChannels to empty array
  const otaChannelsArray = api_key ? [] : (rule.otaChannels || []);
  const result: any = {
    name: rule.name,
    org_id: String(orgId),
    property_id: branchId, // Use branch_id as property_id
    priority: rule.priority,
    isActive: rule.isActive,
    ruletype: 'availability',
    // Send full action array (previously sent in 'actions' key, now sent in 'action' key)
    // API expects 'action' key with array of actions for POST/PATCH requests
    action: rule.action && rule.action.length > 0 
      ? rule.action
      : rule.actions && rule.actions.length > 0
        ? rule.actions
        : rule.actionType
          ? [{ adjustmentType: rule.actionType, adjustmentValue: rule.actionValue }]
          : [{ adjustmentType: 'setStatus', adjustmentValue: 'Open' }],
    roomTypes: rule.roomTypes.map(id => id),
    roomVariants: rule.selectedVariants?.map(v => v.variantId) || [],
    otaChannels: otaChannelsArray,
    ota_channels: otaChannelsArray, // Add snake_case for API compatibility
    applicable_cm_names: rule.applicateCmNames || [],
    applicableTo: {
      startDate: rule.startDate,
      endDate: rule.endDate || null,
      daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : null,
      timeOfDay: rule.timeOfDay || null,
    },
    executionType: {
      processingFrequency: rule.processingFrequency,
      executionDays: rule.executionDays || [],
      executionTime: rule.executionTime || null,
      executionStartDate: rule.executionStartDate || null,
      executionEndDate: rule.executionEndDate || null,
    },
  };

  // Only add api_key if it's not empty
  if (api_key) {
    result.api_key = api_key;
  }

  return result;
}

/**
 * Normalize API response to handle both snake_case and camelCase field names
 */
function normalizeApiRule(rawRule: RawApiAvailabilityRule): ApiAvailabilityRule {
  console.log('Raw API rule:', rawRule.action);
  return {
    id: rawRule.id,
    name: rawRule.name || 'Unnamed Rule',
    org_id: rawRule.org_id || '',
    property_id: (rawRule.property_id ?? rawRule.propertyId) || 0,
    priority: rawRule.priority || 0,
    isActive: rawRule.isActive ?? rawRule.is_active ?? true,
    ruletype: (rawRule.ruletype || rawRule.rule_type || 'availability') as 'availability',
    action: rawRule.action || [],
    roomTypes: (rawRule.roomTypes || rawRule.room_types || []) as number[],
    roomVariants: (rawRule.roomVariants || rawRule.room_variants || []) as number[],
    otaChannels: (rawRule.otaChannels || rawRule.ota_channels || []) as string[],
    applicable_cm_names: (() => {
      // Handle both array and singular string from API
      if (rawRule.applicable_cm_names) return rawRule.applicable_cm_names;
      if (rawRule.applicable_cm_name) return rawRule.applicable_cm_name ? [rawRule.applicable_cm_name] : [];
      if (rawRule.applicateCmNames) return rawRule.applicateCmNames;
      return [];
    })(),
    applicableTo: (rawRule.applicableTo || rawRule.applicable_to || {
      startDate: new Date().toISOString().split('T')[0],
      endDate: null,
      daysOfWeek: null,
      timeOfDay: null,
    }) as ApiAvailabilityRule['applicableTo'],
    executionType: (() => {
      const rawExecution = rawRule.executionType || rawRule.execution_type;
      if (rawExecution) {
        return {
          processingFrequency: (rawExecution.processingFrequency || (rawExecution as any).processing_frequency || 'manual') as any,
          executionDays: rawExecution.executionDays || (rawExecution as any).execution_days || [],
          executionTime: rawExecution.executionTime !== undefined ? rawExecution.executionTime : (rawExecution as any).execution_time !== undefined ? (rawExecution as any).execution_time : null,
          executionStartDate: rawExecution.executionStartDate !== undefined ? rawExecution.executionStartDate : (rawExecution as any).execution_start_date !== undefined ? (rawExecution as any).execution_start_date : null,
          executionEndDate: rawExecution.executionEndDate !== undefined ? rawExecution.executionEndDate : (rawExecution as any).execution_end_date !== undefined ? (rawExecution as any).execution_end_date : null,
        };
      }
      return {
        processingFrequency: 'manual' as const,
        executionDays: [],
        executionTime: null,
        executionStartDate: null,
        executionEndDate: null,
      };
    })(),
    submitted_by: rawRule.submitted_by,
    created_at: rawRule.created_at || rawRule.createdAt,
    updated_at: rawRule.updated_at || rawRule.updatedAt,
    last_executed_at: rawRule.last_executed_at || rawRule.lastExecutedAt,
  };
}

/**
 * Transform availability rule from API format to dialog format
 */
export function transformAvailabilityRuleFromAPI(rawApiRule: RawApiAvailabilityRule): AvailabilityRule {
  // Normalize the API rule first
  const apiRule = normalizeApiRule(rawApiRule);
  // Reverse day mapping
  const reverseDaysMap: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  // Transform days of week back
  const daysOfWeek = apiRule.applicableTo?.daysOfWeek
    ? apiRule.applicableTo.daysOfWeek.map((day: string) => reverseDaysMap[day.toLowerCase()])
    : undefined;

  
  return {
    id: apiRule.id || '',
    name: apiRule.name || 'Unnamed Rule',
    priority: apiRule.priority || 0,
    isActive: apiRule.isActive ?? true,
    roomTypes: (apiRule.roomTypes || []).map((id: number) => String(id)),
    selectedVariants: apiRule.roomVariants?.length > 0 
      ? apiRule.roomVariants.map((id: number) => ({
          roomTypeId: '',
          variantId: String(id),
          variantCode: '',
          variantName: ''
        })) 
      : undefined,
    otaChannels: apiRule.otaChannels || [],
    applicateCmNames: apiRule.applicable_cm_names || [],
    // Transform action array from GET API
    action: apiRule.action && apiRule.action.length > 0
      ? apiRule.action.map((act: any) => {
          const type = act.adjustmentType || act.type || 'setStatus';
          const value = act.adjustmentValue !== undefined ? act.adjustmentValue : (act.value !== undefined ? act.value : 'Open');
          // Map old action type names to new ones
          const mappedType = type === 'setAllotment' ? 'setAvailability' 
            : type === 'adjustAllotment' ? 'adjustAvailability' 
            : type;
          return { adjustmentType: mappedType, adjustmentValue: value };
        })
      : [{ adjustmentType: 'setStatus' as const, adjustmentValue: 'Open' }],
    // Keep actions for backward compatibility
    actions: apiRule.action && apiRule.action.length > 0
      ? apiRule.action.map((act: any) => {
          const type = act.adjustmentType || act.type || 'setStatus';
          const value = act.adjustmentValue !== undefined ? act.adjustmentValue : (act.value !== undefined ? act.value : 'Open');
          const mappedType = type === 'setAllotment' ? 'setAvailability' 
            : type === 'adjustAllotment' ? 'adjustAvailability' 
            : type;
          return { adjustmentType: mappedType, adjustmentValue: value };
        })
      : undefined,
    // Keep legacy fields for backward compatibility (use first action from array)
    actionType: (() => {
      if (apiRule.action && apiRule.action.length > 0) {
        const firstAction = apiRule.action[0];
        const type = firstAction.adjustmentType || firstAction.type || 'setStatus';
        const mappedType = type === 'setAllotment' ? 'setAvailability' 
          : type === 'adjustAllotment' ? 'adjustAvailability' 
          : type;
        return mappedType as any;
      }
      return 'setStatus' as const;
    })(),
    actionValue: (() => {
      if (apiRule.action && apiRule.action.length > 0) {
        const firstAction = apiRule.action[0];
        return firstAction.adjustmentValue !== undefined ? firstAction.adjustmentValue : (firstAction.value !== undefined ? firstAction.value : 'Open');
      }
      return 'Open';
    })(),
    startDate: apiRule.applicableTo?.startDate || new Date().toISOString().split('T')[0],
    endDate: apiRule.applicableTo?.endDate || undefined,
    daysOfWeek: daysOfWeek,
    timeOfDay: apiRule.applicableTo?.timeOfDay || undefined,
    processingFrequency: apiRule.executionType?.processingFrequency || 'manual',
    executionDays: apiRule.executionType?.executionDays || [],
    executionTime: apiRule.executionType?.executionTime || undefined,
    executionStartDate: apiRule.executionType?.executionStartDate || undefined,
    executionEndDate: apiRule.executionType?.executionEndDate || undefined,
    lastRun: apiRule.last_executed_at || undefined,
    createdAt: apiRule.created_at || new Date().toISOString(),
    updatedAt: apiRule.updated_at || new Date().toISOString(),
  };
}
